import { joinPaths } from '@lowerdeck/join-paths';
import { getNexusUrl } from './nexus';

export type EntityParam = { slug: string } | null | undefined;
export type SubPages = (string | null | undefined | object)[];

let makeIdentityPaths = (...prefix: SubPages) => ({
  agents: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    ...subPages: SubPages
  ) => InstancePaths(organization, project, instance, ...prefix, 'agents', ...subPages),

  agent: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    id?: string,
    ...subPages: SubPages
  ) => {
    if (!id) return '#';
    return InstancePaths(organization, project, instance, ...prefix, 'agent', id, ...subPages);
  },

  consumers: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    ...subPages: SubPages
  ) => InstancePaths(organization, project, instance, ...prefix, 'consumers', ...subPages),

  consumer: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    id?: string,
    ...subPages: SubPages
  ) => {
    if (!id) return '#';
    return InstancePaths(
      organization,
      project,
      instance,
      ...prefix,
      'consumer',
      id,
      ...subPages
    );
  },

  actors: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    ...subPages: SubPages
  ) => InstancePaths(organization, project, instance, ...prefix, 'actors', ...subPages),

  actor: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    id?: string,
    ...subPages: SubPages
  ) => {
    if (!id) return '#';
    return InstancePaths(organization, project, instance, ...prefix, 'actor', id, ...subPages);
  },

  identities: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    ...subPages: SubPages
  ) => InstancePaths(organization, project, instance, ...prefix, 'identities', ...subPages),

  identity: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    id?: string,
    ...subPages: SubPages
  ) => {
    if (!id) return '#';
    return InstancePaths(
      organization,
      project,
      instance,
      ...prefix,
      'identity',
      id,
      ...subPages
    );
  },

  delegations: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    ...subPages: SubPages
  ) =>
    InstancePaths(
      organization,
      project,
      instance,
      ...prefix,
      'identity',
      'delegations',
      ...subPages
    ),

  delegation: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    id?: string,
    ...subPages: SubPages
  ) => {
    if (!id) return '#';
    return InstancePaths(
      organization,
      project,
      instance,
      ...prefix,
      'identity',
      'delegation',
      id,
      ...subPages
    );
  },

  delegationConfigs: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    ...subPages: SubPages
  ) =>
    InstancePaths(
      organization,
      project,
      instance,
      ...prefix,
      'identity',
      'delegation-configs',
      ...subPages
    ),

  delegationConfig: (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    id?: string,
    ...subPages: SubPages
  ) => {
    if (!id) return '#';
    return InstancePaths(
      organization,
      project,
      instance,
      ...prefix,
      'identity',
      'delegation-config',
      id,
      ...subPages
    );
  }
});

let instanceIdentityPaths = makeIdentityPaths();

// BlackOS Workforce (portal admin) pages live under /i/:instance/portal/:portalId/...
let portalPath = (
  organization: EntityParam,
  project: EntityParam,
  instance: EntityParam,
  portalId: string | null | undefined,
  ...subPages: SubPages
) => {
  if (!portalId) return '#';
  return InstancePaths(organization, project, instance, 'portal', portalId, ...subPages);
};

let portalChildPath =
  (...segments: string[]) =>
  (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    portalId: string | null | undefined,
    ...subPages: SubPages
  ) =>
    portalPath(organization, project, instance, portalId, ...segments, ...subPages);

let portalEntityPath =
  (segment: string) =>
  (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    portalId: string | null | undefined,
    id: string | null | undefined,
    ...subPages: SubPages
  ) => {
    if (!id) return '#';
    return portalPath(organization, project, instance, portalId, segment, id, ...subPages);
  };

let workforcePortalPaths = {
  overview: portalChildPath(),
  groups: portalChildPath('groups'),
  group: portalEntityPath('group'),
  skills: portalChildPath('resources', 'skills'),
  skillMarketplaces: portalChildPath('resources', 'skills', 'marketplaces'),
  integrations: portalChildPath('resources', 'integrations'),
  highlights: portalChildPath('resources', 'highlights'),
  accessListing: portalEntityPath('access-listing'),
  accessRequests: portalChildPath('access-requests'),
  accounts: portalChildPath('accounts'),
  invites: portalChildPath('accounts', 'invites'),
  account: portalEntityPath('account'),
  agents: portalChildPath('agents'),
  identities: portalChildPath('identities'),
  identity: portalEntityPath('identity'),
  actors: portalChildPath('actors'),
  settings: portalChildPath('settings'),
  clientSettings: portalChildPath('settings', 'client'),
  skillSettings: portalChildPath('settings', 'skills')
};

let InstancePaths = Object.assign(
  (
    organization: EntityParam,
    project: EntityParam,
    instance: EntityParam,
    ...subPages: SubPages
  ) =>
    getNexusUrl('product', () => {
      if (!instance || !project || !organization) return '#';
      return joinPaths(instance.slug, ...subPages);
    }),
  {
    home: (organization: EntityParam, project: EntityParam, instance: EntityParam) =>
      InstancePaths(organization, project, instance, 'home'),
    developer: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'developer', ...subPages),

    security: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'security', ...subPages),
    network: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'network', ...subPages),
    networkFirewalls: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'network', 'firewalls', ...subPages),
    networkEnclaves: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'network', 'enclaves', ...subPages),
    networkFirewall: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'network',
        'firewall',
        id,
        ...subPages
      );
    },
    networkSettings: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) =>
      InstancePaths(
        organization,
        project,
        instance,
        'network',
        'internet-gateway',
        ...subPages
      ),
    providers: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'providers', ...subPages),
    provider: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'provider', id, ...subPages);
    },

    integrations: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'integrations', ...subPages),
    integrationsOverview: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) =>
      InstancePaths(organization, project, instance, 'integrations', 'overview', ...subPages),
    integration: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'integration', id, ...subPages);
    },

    chatConnections: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'chat-connections', ...subPages),
    chatExplorer: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'chat-explorer', ...subPages),
    chatConnection: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'chat-connection',
        id,
        ...subPages
      );
    },
    chat: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'chat', id, ...subPages);
    },
    chatInstance: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'chat-instance', id, ...subPages);
    },
    chatEvent: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'chat-event', id, ...subPages);
    },

    callbacks: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) =>
      InstancePaths(organization, project, instance, 'integrations', 'callbacks', ...subPages),
    callback: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'callback', id, ...subPages);
    },
    incomingWebhook: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'incoming-webhook',
        id,
        ...subPages
      );
    },
    webhookRegistration: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'webhook-registration',
        id,
        ...subPages
      );
    },
    eventDestinations: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'event-destinations', ...subPages),
    eventDestination: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'event-destination',
        id,
        ...subPages
      );
    },
    eventDestinationSettings: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'event-destination',
        id,
        'settings',
        ...subPages
      );
    },
    eventDestinationListeners: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths.eventDestinationSettings(
        organization,
        project,
        instance,
        id,
        'listeners',
        ...subPages
      );
    },
    eventDestinationDeliveries: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths.eventDestination(
        organization,
        project,
        instance,
        id,
        'deliveries',
        ...subPages
      );
    },
    eventDelivery: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'event-delivery', id, ...subPages);
    },
    events: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'events', ...subPages),
    event: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'event', id, ...subPages);
    },
    skills: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'skills', ...subPages),
    skillSettings: (organization: EntityParam, project: EntityParam, instance: EntityParam) =>
      InstancePaths(organization, project, instance, 'skills', 'settings'),
    skill: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'skill', id, ...subPages);
    },
    skillTemplates: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'skills', 'templates', ...subPages),
    skillTemplate: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'skill-template', id, ...subPages);
    },
    skillGroups: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'skills', 'groups', ...subPages),
    skillGroup: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'skill-group', id, ...subPages);
    },
    skillMarketplaces: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'skills', 'marketplaces', ...subPages),
    skillMarketplace: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'skill-marketplace',
        id,
        ...subPages
      );
    },
    skillPlugins: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'skills', 'plugins', ...subPages),
    skillPlugin: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'skill-plugin', id, ...subPages);
    },
    integrationInstance: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'integration-instance',
        id,
        ...subPages
      );
    },

    providerDeployments: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'configurations', ...subPages),
    providerDeployment: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'configurations', id, ...subPages);
    },

    providerImplementations: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) =>
      InstancePaths(organization, project, instance, 'provider-implementations', ...subPages),
    providerImplementation: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'provider-implementation',
        id,
        ...subPages
      );
    },

    providerConfigVaults: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) =>
      InstancePaths(
        organization,
        project,
        instance,
        'configurations',
        'config-vaults',
        ...subPages
      ),
    providerConfigVault: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'configurations',
        'config-vault',
        id,
        ...subPages
      );
    },

    alerts: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'alerts', ...subPages),
    alert: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'alert', id, ...subPages);
    },
    monitors: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'monitors', ...subPages),
    monitor: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'monitor', id, ...subPages);
    },
    protoguard: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'protoguard', ...subPages),
    protoguardFilter: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'protoguard',
        'filter',
        id,
        ...subPages
      );
    },

    providerErrors: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'provider-errors', ...subPages),
    providerError: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'provider-error', id, ...subPages);
    },

    providerRuns: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'provider-runs', ...subPages),
    providerRun: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'provider-run', id, ...subPages);
    },

    providerAuthErrors: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'provider-auth-errors', ...subPages),
    providerAuthError: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'provider-auth-error',
        id,
        ...subPages
      );
    },

    providerAuthEvents: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'provider-auth-events', ...subPages),
    providerAuthEvent: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'provider-auth-event',
        id,
        ...subPages
      );
    },

    workforce: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'workforce', ...subPages),
    portal: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(organization, project, instance, 'portal', id, ...subPages);
    },
    providerTemplates: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'provider-templates', ...subPages),

    workforcePortal: workforcePortalPaths,

    developerClientTokens: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) =>
      InstancePaths(
        organization,
        project,
        instance,
        'developer',
        'client-tokens',
        ...subPages
      ),
    developerAuditLogs: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) =>
      InstancePaths(organization, project, instance, 'developer', 'audit-logs', ...subPages),
    providerListingEditor: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      providerId?: string,
      ...subPages: SubPages
    ) => {
      if (!providerId) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'provider',
        providerId,
        'listing',
        ...subPages
      );
    },

    profile: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'community', 'profile', ...subPages),
    communityProviders: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'community', 'servers', ...subPages),

    externalProviders: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'external-providers', ...subPages),
    customProviders: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'custom-providers', ...subPages),
    customProvider: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'custom-provider',
        id,
        ...subPages
      );
    },

    sandboxMachines: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'sandbox', 'machines', ...subPages),
    sandboxMachine: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'sandbox',
        'machines',
        id,
        ...subPages
      );
    },

    sandboxImages: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'sandbox', 'images', ...subPages),
    sandboxImage: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'sandbox',
        'images',
        id,
        ...subPages
      );
    },
    sandboxImageVersion: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      imageId?: string,
      versionId?: string,
      ...subPages: SubPages
    ) => {
      if (!imageId || !versionId) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'sandbox',
        'images',
        imageId,
        'versions',
        versionId,
        ...subPages
      );
    },

    sandboxDisks: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'sandbox', 'disks', ...subPages),
    sandboxDisk: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'sandbox',
        'disks',
        id,
        ...subPages
      );
    },

    explorer: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'explorer', ...subPages),

    assistant: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'assistant', ...subPages),
    assistantConversation: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'assistant',
        'conversation',
        id,
        ...subPages
      );
    },

    magicMcp: {
      server: (
        organization: EntityParam,
        project: EntityParam,
        instance: EntityParam,
        id?: string,
        ...subPages: SubPages
      ) => {
        if (!id) return '#';
        return InstancePaths(
          organization,
          project,
          instance,
          'magic-mcp/server',
          id,
          ...subPages
        );
      },

      servers: (
        organization: EntityParam,
        project: EntityParam,
        instance: EntityParam,
        ...subPages: SubPages
      ) => {
        return InstancePaths(
          organization,
          project,
          instance,
          'magic-mcp/servers',
          ...subPages
        );
      },

      tokens: (
        organization: EntityParam,
        project: EntityParam,
        instance: EntityParam,
        ...subPages: SubPages
      ) => {
        return InstancePaths(organization, project, instance, 'magic-mcp/tokens', ...subPages);
      },

      connections: (
        organization: EntityParam,
        project: EntityParam,
        instance: EntityParam,
        ...subPages: SubPages
      ) => {
        return InstancePaths(
          organization,
          project,
          instance,
          'magic-mcp/connections',
          ...subPages
        );
      },

      connection: (
        organization: EntityParam,
        project: EntityParam,
        instance: EntityParam,
        id?: string,
        ...subPages: SubPages
      ) => {
        if (!id) return '#';
        return InstancePaths(
          organization,
          project,
          instance,
          'magic-mcp/connection',
          id,
          ...subPages
        );
      },

      groups: (
        organization: EntityParam,
        project: EntityParam,
        instance: EntityParam,
        ...subPages: SubPages
      ) => {
        return InstancePaths(organization, project, instance, 'magic-mcp/groups', ...subPages);
      },

      group: (
        organization: EntityParam,
        project: EntityParam,
        instance: EntityParam,
        ...subPages: SubPages
      ) => {
        return InstancePaths(organization, project, instance, 'magic-mcp/group', ...subPages);
      }
    },

    publicMcp: {
      servers: (
        organization: EntityParam,
        project: EntityParam,
        instance: EntityParam,
        ...subPages: SubPages
      ) => {
        return InstancePaths(
          organization,
          project,
          instance,
          'public-mcp/servers',
          ...subPages
        );
      },

      server: (
        organization: EntityParam,
        project: EntityParam,
        instance: EntityParam,
        id?: string,
        ...subPages: SubPages
      ) => {
        if (!id) return '#';
        return InstancePaths(
          organization,
          project,
          instance,
          'public-mcp/server',
          id,
          ...subPages
        );
      }
    },

    identity: instanceIdentityPaths,

    portalIdentity: (portalId: string | null | undefined) =>
      portalId ? makeIdentityPaths('portal', portalId) : instanceIdentityPaths,

    providerAuthConfigs: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) =>
      InstancePaths(
        organization,
        project,
        instance,
        'configurations',
        'auth-configs',
        ...subPages
      ),
    providerAuthConfig: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      authConfigId?: string,
      ...subPages: SubPages
    ) => {
      if (!authConfigId) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'configurations',
        'auth-config',
        authConfigId,
        ...subPages
      );
    },

    providerConfigs: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => {
      return InstancePaths(
        organization,
        project,
        instance,
        'configurations',
        'configs',
        ...subPages
      );
    },
    providerConfig: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      configId?: string,
      ...subPages: SubPages
    ) => {
      if (!configId) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'configurations',
        'config',
        configId,
        ...subPages
      );
    },

    providerAuthCredentials: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => {
      return InstancePaths(
        organization,
        project,
        instance,
        'configurations',
        'auth-credentials',
        ...subPages
      );
    },
    providerAuthCredential: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      credentialId?: string,
      ...subPages: SubPages
    ) => {
      if (!credentialId) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'configurations',
        'auth-credential',
        credentialId,
        ...subPages
      );
    },

    providerSessions: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'provider-sessions', ...subPages),
    sessionConnections: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'session-connections', ...subPages),
    toolCalls: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'tool-calls', ...subPages),
    providerSession: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'provider-session',
        id,
        ...subPages
      );
    },

    sessionTemplates: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      ...subPages: SubPages
    ) => InstancePaths(organization, project, instance, 'session-templates', ...subPages),
    sessionTemplate: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      id?: string,
      ...subPages: SubPages
    ) => {
      if (!id) return '#';
      return InstancePaths(
        organization,
        project,
        instance,
        'session-template',
        id,
        ...subPages
      );
    },

    setupProvider: (
      organization: EntityParam,
      project: EntityParam,
      instance: EntityParam,
      providerId?: string
    ) => {
      let path = InstancePaths(organization, project, instance, 'setup-provider');
      if (providerId) {
        return `${path}?provider_id=${encodeURIComponent(providerId)}`;
      }
      return path;
    }
  }
);

let AccountPaths = Object.assign(
  (...subPages: SubPages) => getNexusUrl('account', () => joinPaths(...subPages)),
  {
    settings: (...subPages: SubPages) => AccountPaths(...subPages),
    profile: (...subPages: SubPages) => AccountPaths('profile', ...subPages),
    portals: (...subPages: SubPages) => AccountPaths('portals', ...subPages),
    emails: (...subPages: SubPages) => AccountPaths('emails', ...subPages),
    identity: (...subPages: SubPages) => AccountPaths('identity', ...subPages)
  }
);

let SupportPaths = Object.assign(
  (...subPages: SubPages) => getNexusUrl('support', () => joinPaths(...subPages)),
  {
    supportAccess: (organizationId: string | null | undefined) => {
      if (!organizationId) return '#';
      return SupportPaths(organizationId, 'support-access');
    },
    tickets: (organizationId: string | null | undefined, ...subPages: SubPages) => {
      if (!organizationId) return '#';
      return SupportPaths(organizationId, 'tickets', ...subPages);
    },
    newTicket: (organizationId: string | null | undefined) =>
      SupportPaths.tickets(organizationId, 'new'),
    ticket: (
      organizationId: string | null | undefined,
      ticketId: string | null | undefined
    ) => {
      if (!ticketId) return '#';
      return SupportPaths.tickets(organizationId, ticketId);
    }
  }
);

let TrustPaths = Object.assign(
  (...subPages: SubPages) => getNexusUrl('trust', () => joinPaths(...subPages)),
  {
    documents: (organizationId: string | null | undefined) =>
      organizationId ? TrustPaths(organizationId, 'documents') : '#',
    requests: (organizationId: string | null | undefined) =>
      organizationId ? TrustPaths(organizationId, 'requests') : '#'
  }
);

let DocumentsPaths = Object.assign(
  (...subPages: SubPages) => getNexusUrl('documents', () => joinPaths(...subPages)),
  {
    organization: (organizationId: string | null | undefined, ...subPages: SubPages) => {
      if (!organizationId) return '#';
      return DocumentsPaths(organizationId, 'documents', ...subPages);
    }
  }
);

let EnterprisePaths = Object.assign(
  (accountId: string | null | undefined, ...subPages: SubPages) =>
    getNexusUrl('enterprise', () => {
      if (!accountId) return '#';
      return joinPaths(accountId, ...subPages);
    }),
  {
    home: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, ...subPages),
    members: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'members', ...subPages),
    member: (
      accountId: string | null | undefined,
      memberId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!memberId) return '#';

      return EnterprisePaths(accountId, 'members', memberId, ...subPages);
    },
    invites: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'invites', ...subPages),
    invite: (
      accountId: string | null | undefined,
      inviteId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!inviteId) return '#';

      return EnterprisePaths(accountId, 'invites', inviteId, ...subPages);
    },
    access: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'access', ...subPages),
    groups: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'access', ...subPages),
    group: (
      accountId: string | null | undefined,
      groupId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!groupId) return '#';

      return EnterprisePaths(accountId, 'access', 'groups', groupId, ...subPages);
    },
    policies: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'access', ...subPages),
    policy: (
      accountId: string | null | undefined,
      policyId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!policyId) return '#';

      return EnterprisePaths(accountId, 'access', 'policies', policyId, ...subPages);
    },
    workspaces: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'workspaces', ...subPages),
    workspace: (
      accountId: string | null | undefined,
      workspaceId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!workspaceId) return '#';

      return EnterprisePaths(accountId, 'workspaces', workspaceId, ...subPages);
    },
    auth: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'auth', ...subPages),
    domains: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'auth/domains', ...subPages),
    authConnections: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'auth/connections', ...subPages),
    authConnection: (
      accountId: string | null | undefined,
      connectionId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!connectionId) return '#';
      return EnterprisePaths(accountId, 'auth/connections', connectionId, ...subPages);
    },
    authConnectionDirectories: (
      accountId: string | null | undefined,
      connectionId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!connectionId) return '#';
      return EnterprisePaths(
        accountId,
        'auth/connections',
        connectionId,
        'directories',
        ...subPages
      );
    },
    authConnectionUsers: (
      accountId: string | null | undefined,
      connectionId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!connectionId) return '#';
      return EnterprisePaths(
        accountId,
        'auth/connections',
        connectionId,
        'users',
        ...subPages
      );
    },
    authConnectionUser: (
      accountId: string | null | undefined,
      connectionId: string | null | undefined,
      userId: string | null | undefined
    ) => {
      if (!connectionId || !userId) return '#';
      return EnterprisePaths(accountId, 'auth/connections', connectionId, 'users', userId);
    },
    authConnectionGroups: (
      accountId: string | null | undefined,
      connectionId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!connectionId) return '#';
      return EnterprisePaths(
        accountId,
        'auth/connections',
        connectionId,
        'groups',
        ...subPages
      );
    },
    authConnectionGroup: (
      accountId: string | null | undefined,
      connectionId: string | null | undefined,
      groupId: string | null | undefined
    ) => {
      if (!connectionId || !groupId) return '#';
      return EnterprisePaths(accountId, 'auth/connections', connectionId, 'groups', groupId);
    },
    authConnectionRoles: (
      accountId: string | null | undefined,
      connectionId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!connectionId) return '#';
      return EnterprisePaths(
        accountId,
        'auth/connections',
        connectionId,
        'roles',
        ...subPages
      );
    },
    authConnectionRole: (
      accountId: string | null | undefined,
      connectionId: string | null | undefined,
      roleId: string | null | undefined
    ) => {
      if (!connectionId || !roleId) return '#';
      return EnterprisePaths(accountId, 'auth/connections', connectionId, 'roles', roleId);
    },
    authConnectionDirectory: (
      accountId: string | null | undefined,
      connectionId: string | null | undefined,
      directoryId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!connectionId || !directoryId) return '#';
      return EnterprisePaths(
        accountId,
        'auth/connections',
        connectionId,
        'directories',
        directoryId,
        ...subPages
      );
    },
    auditLogs: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'audit-logs', ...subPages),
    billing: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'billing', ...subPages),
    billingWorkspace: (
      accountId: string | null | undefined,
      workspaceId: string | null | undefined,
      ...subPages: SubPages
    ) => {
      if (!workspaceId) return '#';

      return EnterprisePaths(accountId, 'billing', workspaceId, ...subPages);
    },
    settings: (accountId: string | null | undefined, ...subPages: SubPages) =>
      EnterprisePaths(accountId, 'settings', ...subPages)
  }
);

let OrganizationPaths = Object.assign(
  (organization: EntityParam, ...subPages: SubPages) =>
    getNexusUrl('organization', () => {
      if (!organization) return '#';
      return joinPaths(organization.slug, ...subPages);
    }),
  {
    settings: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths(organization, ...subPages),
    billing: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'billing', ...subPages),
    supportAccess: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'support-access', ...subPages),
    auditLogs: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'audit-logs', ...subPages),
    auditLogStreams: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'audit-log-streams', ...subPages),
    members: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'members', ...subPages),
    invites: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'invites', ...subPages),
    teams: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'teams', ...subPages),
    roles: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'roles', ...subPages),
    policies: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'policies', ...subPages),
    outposts: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'outposts', ...subPages),
    projects: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'projects', ...subPages),

    // BlackOS settings area (cloud §7): organization level.
    general: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'general', ...subPages),
    notifications: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'notifications', ...subPages),
    usage: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'usage', ...subPages),
    member: (organization: EntityParam, memberId?: string, ...subPages: SubPages) => {
      if (!memberId) return '#';
      return OrganizationPaths.settings(organization, 'members', memberId, ...subPages);
    },
    policy: (organization: EntityParam, policyId?: string, ...subPages: SubPages) => {
      if (!policyId) return '#';
      return OrganizationPaths.settings(organization, 'policies', policyId, ...subPages);
    },
    oauthApps: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'oauth-apps', ...subPages),
    serviceAccounts: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'service-accounts', ...subPages),
    authorizedApplications: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'authorized-applications', ...subPages),
    cliDevices: (organization: EntityParam, ...subPages: SubPages) =>
      OrganizationPaths.settings(organization, 'cli-devices', ...subPages),

    // Project level (/o/:org/project/:project/...).
    projectSettings: {
      branding: (organization: EntityParam, project: EntityParam, ...subPages: SubPages) =>
        OrganizationPaths.project(organization, project, 'branding', ...subPages),
      retention: (organization: EntityParam, project: EntityParam, ...subPages: SubPages) =>
        OrganizationPaths.project(organization, project, 'retention', ...subPages),
      scm: (organization: EntityParam, project: EntityParam, ...subPages: SubPages) =>
        OrganizationPaths.project(organization, project, 'scm', ...subPages),
      scmConnections: (
        organization: EntityParam,
        project: EntityParam,
        ...subPages: SubPages
      ) => OrganizationPaths.project(organization, project, 'scm', 'connections', ...subPages),
      scmProviders: (organization: EntityParam, project: EntityParam, ...subPages: SubPages) =>
        OrganizationPaths.project(organization, project, 'scm', 'providers', ...subPages),
      authConfig: (organization: EntityParam, project: EntityParam, ...subPages: SubPages) =>
        OrganizationPaths.project(organization, project, 'auth-config', ...subPages),
      toolCalling: (organization: EntityParam, project: EntityParam, ...subPages: SubPages) =>
        OrganizationPaths.project(organization, project, 'tool-calling', ...subPages),
      sessionlessMcp: (
        organization: EntityParam,
        project: EntityParam,
        ...subPages: SubPages
      ) => OrganizationPaths.project(organization, project, 'sessionless-mcp', ...subPages),
      delegationConfig: (
        organization: EntityParam,
        project: EntityParam,
        ...subPages: SubPages
      ) => OrganizationPaths.project(organization, project, 'delegation-config', ...subPages)
    },
    project: (organization: EntityParam, project: EntityParam, ...subPages: SubPages) => {
      if (!project) return '#';
      return OrganizationPaths.settings(organization, 'project', project.slug, ...subPages);
    },

    instance: Object.assign(
      (
        organization: EntityParam,
        project: EntityParam,
        instance: EntityParam,
        ...subPages: SubPages
      ) => {
        if (!organization || !project || !instance) return '#';
        return OrganizationPaths(
          organization,
          'project',
          project.slug,
          'instance',
          instance.slug,
          ...subPages
        );
      },
      {
        compute: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(organization, project, instance, 'compute', ...subPages),

        network: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(organization, project, instance, 'network', ...subPages),
        networkFirewalls: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'network',
            'firewalls',
            ...subPages
          ),
        networkEnclaves: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'network',
            'enclaves',
            ...subPages
          ),
        networkFirewall: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          id?: string,
          ...subPages: SubPages
        ) => {
          if (!id) return '#';
          return OrganizationPaths.instance(
            organization,
            project,
            instance,
            'network',
            'firewall',
            id,
            ...subPages
          );
        },
        networkSettings: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'network',
            'settings',
            ...subPages
          ),

        providerDeployments: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'configurations',
            ...subPages
          ),
        providerDeployment: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          id?: string,
          ...subPages: SubPages
        ) => {
          if (!id) return '#';
          return OrganizationPaths.instance(
            organization,
            project,
            instance,
            'configurations',
            id,
            ...subPages
          );
        },

        providerConfigVaults: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'configurations',
            'config-vaults',
            ...subPages
          ),
        providerConfigVault: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          id?: string,
          ...subPages: SubPages
        ) => {
          if (!id) return '#';
          return OrganizationPaths.instance(
            organization,
            project,
            instance,
            'configurations',
            'config-vault',
            id,
            ...subPages
          );
        },

        providerConfigs: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'configurations',
            'configs',
            ...subPages
          ),
        providerConfig: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          id?: string,
          ...subPages: SubPages
        ) => {
          if (!id) return '#';
          return OrganizationPaths.instance(
            organization,
            project,
            instance,
            'configurations',
            'config',
            id,
            ...subPages
          );
        },

        providerAuthCredentials: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'configurations',
            'auth-credentials',
            ...subPages
          ),
        providerAuthCredential: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          id?: string,
          ...subPages: SubPages
        ) => {
          if (!id) return '#';
          return OrganizationPaths.instance(
            organization,
            project,
            instance,
            'configurations',
            'auth-credential',
            id,
            ...subPages
          );
        },

        providerAuthConfigs: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'configurations',
            'auth-configs',
            ...subPages
          ),
        providerAuthConfig: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          id?: string,
          ...subPages: SubPages
        ) => {
          if (!id) return '#';
          return OrganizationPaths.instance(
            organization,
            project,
            instance,
            'configurations',
            'auth-config',
            id,
            ...subPages
          );
        },

        alerts: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(organization, project, instance, 'alerts', ...subPages),
        alert: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          id?: string,
          ...subPages: SubPages
        ) => {
          if (!id) return '#';
          return OrganizationPaths.instance(
            organization,
            project,
            instance,
            'alert',
            id,
            ...subPages
          );
        },
        monitors: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(organization, project, instance, 'monitors', ...subPages),
        monitor: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          id?: string,
          ...subPages: SubPages
        ) => {
          if (!id) return '#';
          return OrganizationPaths.instance(
            organization,
            project,
            instance,
            'monitor',
            id,
            ...subPages
          );
        },
        protoguard: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'protoguard',
            ...subPages
          ),
        protoguardFilter: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          id?: string,
          ...subPages: SubPages
        ) => {
          if (!id) return '#';
          return OrganizationPaths.instance(
            organization,
            project,
            instance,
            'protoguard',
            'filter',
            id,
            ...subPages
          );
        },

        portals: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(organization, project, instance, 'portals', ...subPages),

        identity: {
          delegations: (
            organization: EntityParam,
            project: EntityParam,
            instance: EntityParam,
            ...subPages: SubPages
          ) =>
            OrganizationPaths.instance(
              organization,
              project,
              instance,
              'identity',
              'delegations',
              ...subPages
            ),
          delegation: (
            organization: EntityParam,
            project: EntityParam,
            instance: EntityParam,
            id?: string,
            ...subPages: SubPages
          ) => {
            if (!id) return '#';
            return OrganizationPaths.instance(
              organization,
              project,
              instance,
              'identity',
              'delegation',
              id,
              ...subPages
            );
          },
          delegationConfigs: (
            organization: EntityParam,
            project: EntityParam,
            instance: EntityParam,
            ...subPages: SubPages
          ) =>
            OrganizationPaths.instance(
              organization,
              project,
              instance,
              'identity',
              'delegation-configs',
              ...subPages
            ),
          delegationConfig: (
            organization: EntityParam,
            project: EntityParam,
            instance: EntityParam,
            id?: string,
            ...subPages: SubPages
          ) => {
            if (!id) return '#';
            return OrganizationPaths.instance(
              organization,
              project,
              instance,
              'identity',
              'delegation-config',
              id,
              ...subPages
            );
          }
        },

        skills: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(organization, project, instance, 'skills', ...subPages),
        skillPlugins: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'skills',
            'plugins',
            ...subPages
          ),
        skillPlugin: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          id?: string,
          ...subPages: SubPages
        ) => {
          if (!id) return '#';
          return OrganizationPaths.instance(
            organization,
            project,
            instance,
            'skills',
            'plugins',
            id,
            ...subPages
          );
        },
        magicMcp: {
          tokens: (
            organization: EntityParam,
            project: EntityParam,
            instance: EntityParam,
            ...subPages: SubPages
          ) =>
            OrganizationPaths.instance(
              organization,
              project,
              instance,
              'magic-mcp',
              'tokens',
              ...subPages
            ),
          groups: (
            organization: EntityParam,
            project: EntityParam,
            instance: EntityParam,
            ...subPages: SubPages
          ) =>
            OrganizationPaths.instance(
              organization,
              project,
              instance,
              'magic-mcp',
              'groups',
              ...subPages
            ),
          group: (
            organization: EntityParam,
            project: EntityParam,
            instance: EntityParam,
            id?: string,
            ...subPages: SubPages
          ) => {
            if (!id) return '#';
            return OrganizationPaths.instance(
              organization,
              project,
              instance,
              'magic-mcp',
              'groups',
              id,
              ...subPages
            );
          }
        },

        developer: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'developer',
            ...subPages
          ),

        // BlackOS settings area (cloud §7) at instance level.
        portalsSettings: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'portals',
            'settings',
            ...subPages
          ),
        skillsGeneral: (
          organization: EntityParam,
          project: EntityParam,
          instance: EntityParam,
          ...subPages: SubPages
        ) =>
          OrganizationPaths.instance(
            organization,
            project,
            instance,
            'skills',
            'general',
            ...subPages
          )
      }
    )
  }
);

export let WelcomePaths = Object.assign(
  (...subPages: SubPages) => getNexusUrl('welcome', () => joinPaths(...subPages)),
  {
    onboarding: (i: { organizationId: string }) => {
      let inner = WelcomePaths('onboarding');
      let search = new URLSearchParams({ organization_id: i.organizationId });
      return `${inner}?${search.toString()}`;
    },
    claim: (i: { organizationId: string; claimToken: string }) => {
      let inner = WelcomePaths('claim');
      let search = new URLSearchParams({
        org_id: i.organizationId,
        claim_token: i.claimToken
      });
      return `${inner}?${search.toString()}`;
    },
    project: (i: { organizationId: string }) => {
      let inner = WelcomePaths('project');
      let search = new URLSearchParams({ organization_id: i.organizationId });
      return `${inner}?${search.toString()}`;
    },
    createProject: (i: { organizationId: string }, ...subPages: SubPages) => {
      let inner = WelcomePaths('create-project');
      let search = new URLSearchParams({ organization_id: i.organizationId });
      return `${inner}?${search.toString()}`;
    },
    setupProvider: (i: { organizationId: string; projectId: string }) => {
      let inner = WelcomePaths('setup-provider');
      let search = new URLSearchParams({
        organization_id: i.organizationId,
        project_id: i.projectId
      });
      return `${inner}?${search.toString()}`;
    }
  }
);

// The nexus root is an absolute URL, which joinPaths would turn into a relative path
// (`/https://host/...`), so the sub pages have to be joined onto its pathname instead.
let joinNexusRoot = (root: string, ...subPages: SubPages) => {
  try {
    let url = new URL(root);
    let [pathname, search] = joinPaths(url.pathname, ...subPages).split('?');
    url.pathname = pathname;
    if (search) url.search = search;
    return url.toString();
  } catch {
    return joinPaths(root, ...subPages);
  }
};

export let IndexPaths = Object.assign(
  (...subPages: SubPages) => getNexusUrl('index', () => joinPaths(...subPages)),
  {
    globalIndex: (...subPages: SubPages) => {
      let nexusRoot = (window as any).nexusRoot as string | undefined;
      if (nexusRoot) return joinNexusRoot(nexusRoot, ...subPages);

      return getNexusUrl('index', () => joinPaths(...subPages));
    },

    globalHome: (...subPages: SubPages) => {
      let nexusRoot = (window as any).nexusRoot as string | undefined;
      if (nexusRoot) return joinNexusRoot(nexusRoot, 'home', ...subPages);

      return getNexusUrl('index', () => joinPaths('home', ...subPages));
    }
  }
);

export let PLACEHOLDER_INSTANCE_ENTITY = { id: 'a', slug: 'a' };

export let PLACEHOLDER_INSTANCE_PARAMS = [
  PLACEHOLDER_INSTANCE_ENTITY,
  PLACEHOLDER_INSTANCE_ENTITY,
  PLACEHOLDER_INSTANCE_ENTITY
] as const;

let dashboardInstanceRedirect = (
  dashboardUrl: string,
  fullInstancePath: string,
  opts?: { organizationId?: string }
) => {
  if (!fullInstancePath || fullInstancePath === '#' || !fullInstancePath.startsWith('/i/')) {
    return fullInstancePath;
  }

  let queryIndex = fullInstancePath.indexOf('?');
  let pathname = queryIndex >= 0 ? fullInstancePath.slice(0, queryIndex) : fullInstancePath;
  let query = queryIndex >= 0 ? fullInstancePath.slice(queryIndex + 1) : '';

  let segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'i' || segments.length < 2) return fullInstancePath;

  let prefix =
    segments.length >= 4
      ? `/${segments.slice(0, 4).join('/')}`
      : `/${segments.slice(0, 2).join('/')}`;

  let resPath = pathname.slice(prefix.length);

  if (query) {
    resPath = resPath ? `${resPath}?${query}` : `?${query}`;
  }

  let dashUrl = new URL(dashboardUrl);
  if (resPath.length > 0) dashUrl.searchParams.set('path', resPath);
  if (opts?.organizationId) dashUrl.searchParams.set('organization_id', opts.organizationId);

  return dashUrl.toString();
};

InstancePaths.providerAuthCredentials = OrganizationPaths.instance.providerAuthCredentials;
InstancePaths.providerAuthCredential = OrganizationPaths.instance.providerAuthCredential;

export let Paths = {
  join: joinPaths,

  instance: InstancePaths,
  account: AccountPaths,
  documents: DocumentsPaths,
  trust: TrustPaths,
  support: SupportPaths,
  enterprise: EnterprisePaths,
  organization: OrganizationPaths,
  welcome: WelcomePaths,
  dashboardInstanceRedirect,
  index: IndexPaths
};
